import './MessageChat.scss';

import { CommentOutlined, SendOutlined, SyncOutlined } from '@ant-design/icons';
import { useSelect } from '@refinedev/antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Flex, Form, Mentions, Spin } from 'antd';
import { OptionProps } from 'antd/lib/mentions';
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import AnchorableDrawer from '@/components/general/Drawers/AnchorableDrawer';
import { ENTITY_TYPE } from '@/model/General/MessageChat';
import { useRepositoriesContext } from '@/repositories/RepositoriesContext';

import MessageChatRow from './MessageChatRow';

interface MessageChatProps {
  entityType: ENTITY_TYPE;
  entityId: number;
  title: ReactNode;
}

interface UserOption {
  id: number;
  first_name: string;
  last_name: string;
  username?: string;
}

interface MentionOption {
  label: string;
  value: string;
  key?: string;
  dataId: number;
}

const MessageChat = (props: MessageChatProps) => {
  const { t } = useTranslation();
  const { entityType, entityId, title } = props;
  const { messageChatRepository } = useRepositoriesContext();
  const [form] = Form.useForm();
  const [open, setOpen] = useState(false);
  const [anchoredDrawer, setAnchoredDrawer] = useState(false);
  const [mentionIds, setMentionIds] = useState<number[]>([]);
  const messagesContainer = useRef<HTMLDivElement>(null);
  const [shouldScrollToBottom, setShouldScrollToBottom] = useState(false);
  const messagesQueryKey = useMemo(
    () => ['messageChat', entityType, entityId] as const,
    [entityType, entityId],
  );
  const requestScrollToBottom = useCallback(() => {
    setShouldScrollToBottom(true);
  }, []);
  /**
   * Metadata:
   * - Proposito: delegar cache, carga y refetch de mensajes a TanStack Query
   *   manteniendo la logica HTTP/transformacion dentro del repository.
   */
  const queryClient = useQueryClient();
  const {
    data: messages = [],
    isFetching: loadingMessages,
    refetch: refetchMessages,
  } = useQuery({
    queryKey: messagesQueryKey,
    queryFn: () => messageChatRepository.getMessages(entityType, entityId),
    enabled: !!entityId,
    refetchInterval: 30000, // Refrescar cada 30 segundos para obtener nuevos mensajes
    retry: false, // No reintentar automáticamente en caso de error
  });
  const postMessageMutation = useMutation({
    mutationFn: (values: { message_to_send: string }) =>
      messageChatRepository.postMessage({
        entity_type: entityType,
        entity_id: entityId,
        text: values.message_to_send,
        destination_users: mentionIds ?? [],
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: messagesQueryKey }).then(() => {
        form.resetFields();
        setMentionIds([]);
        requestScrollToBottom();
      });
    },
  });
  const handleSubmit = useCallback(
    (values: { message_to_send: string }) => {
      postMessageMutation.mutate(values);
    },
    [postMessageMutation],
  );

  useEffect(() => {
    if (!loadingMessages && messages.length > 0) {
      requestScrollToBottom();
    }
  }, [loadingMessages, messages.length, requestScrollToBottom]);

  /**
   * Manejo de menciones
   */

  const { selectProps: usersSelectProps } = useSelect<UserOption>({
    resource: 'users',
    optionValue: 'id',
    optionLabel: user => `${user.first_name} ${user.last_name}`,
    pagination: {
      mode: 'off',
      pageSize: 3000,
    },
  });
  const mentionOptions = useMemo<MentionOption[]>(
    () =>
      (usersSelectProps.options ?? []).reduce<MentionOption[]>((acc, option) => {
        let normalizedLabel = '';
        const optionLabel = (option as OptionProps).label;

        if (typeof optionLabel === 'string') {
          normalizedLabel = optionLabel;
        } else if (optionLabel && typeof optionLabel === 'object' && 'toString' in optionLabel) {
          normalizedLabel = optionLabel.toString();
        }

        const rawId = typeof option.value === 'number' ? option.value : Number(option.value);

        if (!normalizedLabel || Number.isNaN(rawId)) {
          return acc;
        }

        acc.push({
          label: normalizedLabel,
          value: normalizedLabel,
          key: option.value?.toString(),
          dataId: rawId,
        });

        return acc;
      }, [] as MentionOption[]),
    [usersSelectProps.options],
  );
  const messageValue = Form.useWatch('message_to_send', form) ?? '';
  const escapeRegExp = (text: string) => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  useEffect(() => {
    if (!messageValue) {
      setMentionIds([]);

      return;
    }

    const ids = mentionOptions.reduce<Set<number>>((acc, option) => {
      const normalizedLabel = option.label.trim();

      if (!normalizedLabel) {
        return acc;
      }

      const pattern = new RegExp(`@${escapeRegExp(normalizedLabel)}(?=$|\\s|[.,!?;:])`);

      if (pattern.test(messageValue)) {
        acc.add(option.dataId);
      }

      return acc;
    }, new Set<number>());

    setMentionIds(Array.from(ids));
  }, [messageValue, mentionOptions]);

  // Ordenar mensajes por fecha
  const messageToShow = useMemo(() => {
    if (!messages) {
      return [];
    }

    const getTime = (date?: string) => {
      if (!date) {
        return Number.POSITIVE_INFINITY;
      }

      const parsedTime = new Date(date).getTime();

      return Number.isNaN(parsedTime) ? Number.POSITIVE_INFINITY : parsedTime;
    };

    return [...messages].sort((a, b) => getTime(a.date) - getTime(b.date));
  }, [messages]);

  useEffect(() => {
    if (!shouldScrollToBottom) {
      return;
    }

    if (!open || loadingMessages || !messagesContainer.current || messages.length === 0) {
      return;
    }

    messagesContainer.current.scrollTop = messagesContainer.current.scrollHeight;
    setShouldScrollToBottom(false);
  }, [shouldScrollToBottom, open, loadingMessages, messages]);

  return (
    <div>
      <Badge count={messageToShow.length} size="small">
        <Button
          icon={<CommentOutlined />}
          type={open ? 'primary' : 'default'}
          onClick={() => setOpen(!open)}
        />
      </Badge>
      <AnchorableDrawer
        classNames={{ body: 'MessageChatDrawerBody' }}
        className="MessageChatDrawer"
        title={
          <Flex justify="space-between" align="center" style={{ width: '100%' }}>
            {title} {entityId ? `#${entityId}` : ''}{' '}
            <Button
              size="small"
              icon={<SyncOutlined />}
              onClick={() => void refetchMessages().then(() => requestScrollToBottom())}
            />
          </Flex>
        }
        width={500}
        open={open}
        setOpen={setOpen}
        anchorableDrawer={anchoredDrawer}
        setAnchorableDrawer={setAnchoredDrawer}>
        <div className="MessageChatDrawerMessages" ref={messagesContainer}>
          {loadingMessages ? (
            <Spin />
          ) : (
            messageToShow.map(message => <MessageChatRow key={message.id} message={message} />)
          )}
        </div>
        <Form form={form} className="MessageChatDrawerInputContainer" onFinish={handleSubmit}>
          <Form.Item name={'message_to_send'} className="MessageChatDrawerInput">
            <Mentions
              disabled={postMessageMutation.isPending}
              onPressEnter={e => {
                if (!e.shiftKey) {
                  e.preventDefault();
                  form.submit();
                }
              }}
              autoSize={{ minRows: 2 }}
              placeholder={`${t('Escribe tu mensaje aquí')}...`}
              options={mentionOptions}
              loading={usersSelectProps.loading}
              filterOption={(search, option) => {
                const normalizedSearch = search.toLowerCase();

                if (!normalizedSearch) {
                  return true;
                }

                const normalizedLabel = (option as OptionProps)?.label
                  ? (option as OptionProps).label.toString().toLowerCase()
                  : '';

                return normalizedLabel.includes(normalizedSearch);
              }}
            />
          </Form.Item>
          <Button
            className="MessageChatDrawerSendButton"
            type="primary"
            htmlType="submit"
            icon={<SendOutlined />}
            loading={postMessageMutation.isPending}
            disabled={postMessageMutation.isPending}
          />
        </Form>
      </AnchorableDrawer>
    </div>
  );
};

export default MessageChat;
